import React from "react";

import fetchSingleton from "./fetchSingleton";
import { useAuth } from "@/lib/useAuth";
import { useEventSubscriber, emit } from "@/lib/useEventEmitter";
import { ApiResponseDto } from "@/types/api-types";

type optionsT = {
  anonymous?: boolean;
  noCache?: boolean;
  enabled?: boolean;
  cacheForever?: number;
};

type urlT = string | undefined | null | 0 | false;

export const useDataRaw = <T>(
  url: urlT,
  options?: optionsT,
): [
  T | undefined,
  boolean,
  { error?: unknown; meta?: { [key: string]: unknown }; refresh: () => Promise<void> },
] => {
  const [data, setData] = React.useState<T | undefined>(undefined);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<unknown>(undefined);
  const fetchProcessRef = React.useRef<ReturnType<typeof fetchSingleton> | undefined>(
    undefined,
  );
  const { idToken } = useAuth();

  const refreshHandlerRef =
    React.useRef<() => void | Promise<() => void> | undefined>(undefined);

  useEventSubscriber("forceDataRefresh", () => {
    refreshHandlerRef.current?.();
  });

  const fetchData = React.useCallback(async () => {
    if (
      url &&
      (options?.enabled || options?.enabled === undefined) &&
      (idToken || options?.anonymous)
    ) {
      if (process.env.NODE_ENV !== "development" && url.startsWith("/")) {
        url = url + ".json";
        const [path, query] = url.split("?");
        if (query) url = path + encodeURIComponent("?" + query);
      }

      const fetchData = async () => {
        fetchProcessRef.current?.cancel();

        try {
          setLoading(true);
          const fetchProcess = (fetchProcessRef.current = fetchSingleton(url as string, {
            headers: {
              "Content-type": "application/json",
              Authorization: idToken ? `Bearer ${idToken}` : "",
            },
            noCache: options?.noCache,
          }));
          const responseJson = (await fetchProcess) as T;
          setData(responseJson);
        } catch (err) {
          // Check if error is an AbortError
          if (err instanceof Error && err.name === "AbortError") {
            // Do nothing
          } else {
            setError(err instanceof Error ? err.message : "An unknown error occurred");
          }
        }
        fetchProcessRef.current = undefined;
        setLoading(false);
      };

      fetchData().then();

      refreshHandlerRef.current = () => {
        fetchData().then();
      };
    } else {
      if (fetchProcessRef.current) fetchProcessRef.current?.cancel();
      fetchProcessRef.current = undefined;
      refreshHandlerRef.current = undefined;
      setLoading(false);
      setData(undefined);
    }
  }, [url, idToken, options?.anonymous, options?.enabled]);

  React.useEffect(() => {
    fetchData().then();

    return () => {
      fetchProcessRef.current?.cancel();
      fetchProcessRef.current = undefined;
      refreshHandlerRef.current = undefined;
    };
  }, [fetchData]);

  return [data, loading, { error, refresh: fetchData }] as [
    T | undefined,
    boolean,
    { error?: unknown; refresh: () => Promise<void> },
  ];
};

type useDataList = <T>(
  url: urlT,
  options?: optionsT,
) => [
  T[] | undefined,
  boolean,
  {
    status: string;
    subStatus: string;
    error?: unknown;
    meta: { [key: string]: unknown };
    refresh: () => Promise<void>;
  },
];

export const useDataList: useDataList = <T>(url: urlT, options?: optionsT) => {
  const [data, loading, { error, refresh }] = useDataRaw<ApiResponseDto<T>>(url, options);
  return data?.status === "OK"
    ? [
        data?.list,
        loading,
        {
          status: data?.status,
          // @ts-expect-error Varying on backend
          subStatus: data?.subStatus || data?.sub_status,
          error: error,
          meta: data?.meta || {},
          refresh,
        },
      ]
    : ([
        undefined,
        loading,
        {
          status: data?.status,
          subStatus: data?.subStatus,
          error: data || error,
          meta: data?.meta || {},
          refresh,
        },
      ] as [
        T[] | undefined,
        boolean,
        {
          status: string;
          subStatus: string;
          error?: unknown;
          meta: { [key: string]: unknown };
          refresh: () => Promise<void>;
        },
      ]);
};

type UseData = <T>(
  url: urlT,
  options?: optionsT,
) => [
  T | undefined,
  boolean,
  {
    status: string;
    subStatus: string;
    error?: unknown;
    meta: { [key: string]: unknown };
    refresh: () => Promise<void>;
  },
];

export const useData: UseData = <T>(url: urlT, options?: optionsT) => {
  const [dataList, loading, { status, subStatus, error, meta, refresh }] = useDataList<T>(
    url,
    options,
  );
  return [dataList?.[0], loading, { status, subStatus, error, meta, refresh }] as [
    T | undefined,
    boolean,
    {
      status: string;
      subStatus: string;
      error?: unknown;
      meta: { [key: string]: unknown };
      refresh: () => Promise<void>;
    },
  ];
};

export const triggerDataRefresh = () => {
  emit("forceDataRefresh");
};
